import { useEffect, useState } from 'react';
import { goBack, resetNav } from '../../app/navigation';
import { displayAddress } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { billingStore, refreshBilling } from '../../state/billingStore';
import { useStore } from '../../state/createStore';
import { deleteInspectionAction } from '../../state/deleteInspection';
import { inspectionStore } from '../../state/inspectionStore';
import { movedToOf, undoMoveOutAction } from '../../state/inspectionTransfer';
import { Button, Card, ConfirmSheet, Icon, showToast, TopBar } from '../../ui/components';
import { formatClock, formatDate } from '../../ui/hooks';

// Every screen of an inspection that was exported to another device lands here instead (src/app/AppShell.tsx):
// the copy on this device is view-only, the work goes on wherever the file is imported (Owner 2026-10-20). It
// says when and into which file it went, whether another device has taken it yet (the server knows the
// device, never the content), and offers the two things that make sense: carry on here after all (only while
// nobody else has it), or delete this copy.
export function MovedOutScreen(props: { inspectionId: string }) {
  const { t } = useT();
  const inspection = useStore(inspectionStore, (s) => s.inspections.find((i) => i.id === props.inspectionId) ?? null);
  useStore(billingStore, (s) => s.server);
  const [confirm, setConfirm] = useState<'undo' | 'delete' | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void refreshBilling().catch(() => undefined);
  }, []);
  if (!inspection || !inspection.movedOut) return null;
  const move = inspection.movedOut;
  const holder = movedToOf(inspection.id);
  const device = holder ? holder.label || t('otherDevices.someDevice') : null;

  async function undo() {
    setConfirm(null);
    setBusy(true);
    try {
      const result = await undoMoveOutAction(props.inspectionId);
      if (result.kind === 'undone') showToast(t('moved.undone'));
      else if (result.reason === 'heldElsewhere') showToast(t('moved.undoRefused', { device: movedToOf(props.inspectionId)?.label || t('otherDevices.someDevice') }), 'info', 7000);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setConfirm(null);
    setBusy(true);
    try {
      await deleteInspectionAction(props.inspectionId);
      resetNav({ screen: 'inspectionList' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen" data-testid="moved-out-screen">
      <TopBar title={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
      <div className="screen-body">
        <Card testId="moved-out-card">
          <div className="stack">
            <span className="icon-tile">
              <Icon name="drive_file_move" />
            </span>
            <h2 className="card-title">{t('moved.title')}</h2>
            <p className="card-body" data-testid="moved-out-body">
              {t('moved.body', { date: `${formatDate(move.at)} ${formatClock(move.at)}`, file: move.fileName })}
            </p>
            <p className="card-note" data-testid="moved-out-holder">
              {device ? t('moved.takenBy', { device }) : t('moved.waiting')}
            </p>
            {device ? null : (
              <Button variant="secondary" icon="undo" onClick={() => setConfirm('undo')} disabled={busy} testId="moved-out-undo">
                {t('moved.undo')}
              </Button>
            )}
            <Button variant="text" icon="delete" onClick={() => setConfirm('delete')} disabled={busy} testId="moved-out-delete">
              {t('moved.deleteCopy')}
            </Button>
          </div>
        </Card>
      </div>
      {confirm === 'undo' ? (
        <ConfirmSheet title={t('moved.undoTitle')} body={t('moved.undoBody')} confirmLabel={t('moved.undo')} onConfirm={() => void undo()} onCancel={() => setConfirm(null)} testId="moved-out-undo-confirm" />
      ) : null}
      {confirm === 'delete' ? (
        <ConfirmSheet
          title={t('inspections.deleteTitle', { address: displayAddress(inspection, t('inspection.noAddress')) })}
          body={t('inspections.deleteMovedBody')}
          confirmLabel={t('inspections.delete')}
          danger
          onConfirm={() => void remove()}
          onCancel={() => setConfirm(null)}
          testId="moved-out-delete-confirm"
        />
      ) : null}
    </div>
  );
}
